
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';
import * as Sentry from '@sentry/react';
import { mapGenderToLabel } from '@/lib/gender';

export interface Participant {
  userId: string;
  experimentId: string;
  userExperimentId: string;
  ageGroup: string;
  gender: string;
  ethnicity: string;
  bmiGroup: string;
  tags: string[];
  glycemicType: string;
  productLogs: number;
  tier: string;
  registeredAt?: string | null;
  completedAt?: string | null;
}

interface LoggedProduct {
  food_log_id: string;
  name: string;
  started_at_utc: string;
  category: string;
  servings: number;
  carbs: number;
  fiber: number;
  image_url: string;
  branded_food_id: number;
  step_id: string;
  inserted_at: string;
  experiment_id: string;
}

interface ParticipantDetails extends Participant {
  height: string;
  weight: string;
  activityLevel: string;
  dietaryPreferences: string[];
  medicalConditions: string[];
  productTests: Array<{
    id: string;
    name: string;
    date: string;
    glycemicIndex: number;
    glucoseSpike: string;
    satiety: number;
    experimentId: string | null;
  }>;
  loggedProducts: LoggedProduct[];
}

interface ApiParticipant {
  user_experiment_id: string;
  experiment_id: string;
  user_id: string;
  tier: string;
  registered_at: string;
  completed_at: string;
  active: boolean;
  biometric_profile: {
    glycemic_type: string;
    gender: string;
    ethnicity: string;
    height_cm: number;
    weight_kg: number;
    bmi: number;
    age: number;
    waist_cm: number;
    hip_cm: number;
    waist_to_hip_ratio: number;
    is_complete_profile: boolean;
    updated_at: string;
  } | null;
  diet: string[];
  diet_exclusions: string[];
  user_diets: string[];
}

interface MealImpact {
  meal_id: string;
  food_log_names: string[];
  meal_timestamp_utc: string;
  start_glucose_level: number;
  peak_glucose_level: number;
  time_to_peak: number;
  time_to_return: string | null;
  iauc: number;
  indicative_glycemic: number;
  glycemic_range: string | null;
  experiment_valid: boolean;
  experiment_id: string;
}

interface ApiParticipantDetails extends ApiParticipant {
  meal_impacts: MealImpact[];
}

interface ApiResponse {
  participants: ApiParticipant[];
  total_count: number;
  limit: number;
  offset: number;
}

function mapEthnicityToLabel(ethnicity: string): string {
  const ethnicityMap: Record<string, string> = {
    'AFRICAN': 'African',
    'ASIAN': 'Asian',
    'EUROPEAN': 'European',
    'LATIN_AMERICAN': 'Latin American',
    'MIDDLE_EASTERN': 'Middle Eastern',
    'NORTH_AMERICAN_INDIGENOUS': 'North American Indigenous',
    'OCEANIAN': 'Oceanian',
    'MIXED_HERITAGE': 'Mixed Heritage',
    'PREFER_NOT_TO_ANSWER': 'Prefer not to answer'
  };

  return ethnicityMap[ethnicity] || ethnicity;
}

function mapGlycemicTypeToLabel(glycemicType: string): string {
  const glycemicTypeMap: Record<string, string> = {
    'DEFAULT': 'Non-diabetic',
    'DIABETIC': 'Diabetic',
    'DIABETIC1': 'Type 1 Diabetic',
    'DIABETIC2': 'Type 2 Diabetic',
    'PREDIABETIC': 'Prediabetic'
  };

  return glycemicTypeMap[glycemicType] || 'Non-diabetic';
}

function formatParticipant(participant: ApiParticipant): Participant {
  const { biometric_profile } = participant;

  // Determine age group
  let ageGroup = '-';
  if (biometric_profile?.age) {
    if (biometric_profile.age < 25) ageGroup = '18-24';
    else if (biometric_profile.age < 35) ageGroup = '25-34';
    else if (biometric_profile.age < 45) ageGroup = '35-44';
    else if (biometric_profile.age < 55) ageGroup = '45-54';
    else if (biometric_profile.age < 65) ageGroup = '55-64';
    else ageGroup = '65+';
  }

  // Determine BMI group
  let bmiGroup = '-';
  if (biometric_profile?.bmi) {
    if (biometric_profile.bmi < 18.5) bmiGroup = 'Underweight';
    else if (biometric_profile.bmi < 25) bmiGroup = 'Normal';
    else if (biometric_profile.bmi < 30) bmiGroup = 'Overweight';
    else bmiGroup = 'Obese';
  }

  // Map glycemic type to label - treat null/undefined as Non-diabetic
  const glycemicType = mapGlycemicTypeToLabel(biometric_profile?.glycemic_type || 'DEFAULT');

  // Build tags array with diet-related information and diabetic indicator
  const tags: string[] = [];

  // Add glycemic type tag if not Non-diabetic
  if (glycemicType !== 'Non-diabetic') {
    tags.push(glycemicType);
  }

  // Add diet-related tags
  if (participant.diet && participant.diet.length > 0) {
    tags.push(...participant.diet);
  }

  if (participant.diet_exclusions && participant.diet_exclusions.length > 0) {
    tags.push(...participant.diet_exclusions.map(exclusion => `No ${exclusion}`));
  }

  if (participant.user_diets && participant.user_diets.length > 0) {
    tags.push(...participant.user_diets);
  }

  return {
    userId: participant.user_id,
    experimentId: participant.experiment_id,
    userExperimentId: participant.user_experiment_id,
    ageGroup,
    gender: mapGenderToLabel(biometric_profile?.gender),
    ethnicity: biometric_profile?.ethnicity ? mapEthnicityToLabel(biometric_profile.ethnicity) : '-',
    bmiGroup,
    glycemicType,
    tags,
    productLogs: 0, // Not provided by API
    tier: participant.tier || (participant.active ? 'Active' : 'Inactive'),
  };
}

function formatParticipantDetails(participant: ApiParticipantDetails): ParticipantDetails {
  const baseParticipant = formatParticipant(participant);
  const { biometric_profile } = participant;

  // Format height and weight
  const height = biometric_profile?.height_cm ? `${biometric_profile.height_cm} cm` : 'N/A';
  const weight = biometric_profile?.weight_kg ? `${biometric_profile.weight_kg} kg` : 'N/A';

  // Convert meal impacts to product tests format for compatibility
  const productTests = (participant.meal_impacts || []).map(impact => ({
    id: impact.meal_id,
    name: impact.food_log_names.join(', '),
    date: new Date(impact.meal_timestamp_utc).toLocaleString(),
    experimentId: impact.experiment_id,
    glycemicIndex: Math.round(impact.indicative_glycemic * 100), // Convert to percentage
    glucoseSpike: `${impact.peak_glucose_level - impact.start_glucose_level} mg/dL`,
    satiety: null, // Not provided in meal impacts
  }));

  // Convert meal impacts to logged products format for backwards compatibility
  const loggedProducts = (participant.meal_impacts || []).map(impact => ({
    food_log_id: impact.meal_id,
    name: impact.food_log_names.join(', '),
    started_at_utc: impact.meal_timestamp_utc,
    category: '',
    servings: 1,
    carbs: 0,
    fiber: 0,
    image_url: '',
    branded_food_id: 0,
    step_id: '',
    inserted_at: impact.meal_timestamp_utc,
    experiment_id: impact.experiment_id,
  }));

  return {
    ...baseParticipant,
    height,
    weight,
    activityLevel: 'N/A', // Not provided by API
    dietaryPreferences: [
      ...(participant.diet || []),
      ...(participant.user_diets || [])
    ],
    medicalConditions: biometric_profile?.glycemic_type === 'DIABETIC' ? ['Diabetic'] : [],
    productTests,
    loggedProducts,
  };
}

export const useParticipants = (limit: number = 50, offset: number = 0) => {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: ['participants', organization?.id, limit, offset],
    queryFn: async (): Promise<{ participants: Participant[]; totalCount: number }> => {
      if (!organization?.id) {
        return { participants: [], totalCount: 0 };
      }

      console.log('useParticipants: Fetching participants from new API endpoint...');

      try {
        // Get experiment IDs for this organization
        const { data: orgExperiments, error: orgError } = await supabase
          .from('organization_experiments')
          .select('experiment_id')
          .eq('organization_id', organization.id);

        if (orgError) {
          throw new Error(orgError.message || 'Failed to fetch organization experiments');
        }

        if (!orgExperiments || orgExperiments.length === 0) {
          return { participants: [], totalCount: 0 };
        }

        const experimentIds = orgExperiments.map(oe => oe.experiment_id);

        // Call the new participants API endpoint
        const experimentIdsQuery = experimentIds.map(id => `experiment_ids=${encodeURIComponent(id)}`).join('&');
        const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
          body: {
            method: 'GET',
            endpoint: '/api/v2/participants',
            queryString: `${experimentIdsQuery}&limit=${limit}&offset=${offset}`
          },
        });

        if (error) {
          Sentry.captureException(error);
          throw new Error(error.message || 'Failed to fetch participants');
        }

        if (apiResponse.status !== 200) {
          throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch participants'}`);
        }

        const responseData = apiResponse.data as ApiResponse;
        console.log('useParticipants: Participants data:', responseData);

        const formattedParticipants = responseData.participants.map(formatParticipant);

        return {
          participants: formattedParticipants,
          totalCount: responseData.total_count
        };
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    enabled: !!organization?.id,
  });
};

export const useParticipantDetails = (userId: string) => {
  return useQuery({
    queryKey: ['participant', userId],
    queryFn: async (): Promise<ParticipantDetails> => {
      console.log('useParticipantDetails: Fetching participant details for:', userId);

      try {
        // Call the tastermonial-api edge function for participant details
        const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
          body: {
            method: 'GET',
            endpoint: `/api/v2/participants/${userId}`,
          },
        });

        if (error) {
          Sentry.captureException(error);
          throw new Error(error.message || 'Failed to fetch participant details');
        }

        if (apiResponse.status !== 200) {
          throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch participant details'}`);
        }

        const participantData = apiResponse.data as ApiParticipantDetails;
        console.log('useParticipantDetails: Participant details:', participantData);

        return formatParticipantDetails(participantData);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    enabled: !!userId,
  });
};
